<section class="admin-action-detail">
  @if (isLoading()) {
  <div class="state-panel" role="status" aria-live="polite">
    <app-progress-spinner [diameter]="56" />
    <h1 class="state-panel__title">Loading audit entry…</h1>
    <p class="state-panel__message">Hang tight while we fetch this admin action.</p>
  </div>
  } @else if (loadError()) {
  <div class="state-panel" role="alert">
    <span
      class="state-panel__icon"
      [class.state-panel__icon--error]="!notFound()"
      aria-hidden="true"
    >
      <app-icon
        [name]="errorIcon()"
        size="custom"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
    </span>
    <h1 class="state-panel__title">{{ errorTitle() }}</h1>
    <p class="state-panel__message">{{ loadError() }}</p>
    <a class="primary-button state-panel__button fw-600" routerLink="/activity-logs">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Audit Log</span>
    </a>
  </div>
  } @else if (detail(); as detail) {
  <nav class="breadcrumb flex align-center gap-8 fs-12 mb-15" aria-label="Breadcrumb">
    <a
      class="breadcrumb__link fw-600"
      routerLink="/activity-logs"
      aria-label="Return to the audit log"
      >Audit Log</a
    >
    @if (patient(); as patient) {
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <!-- Patient crumb: plain text until a patient screen exists to link to. -->
    <span>{{ patient.displayName }}</span>
    }
    <app-icon
      class="breadcrumb__icon"
      name="chevron_right"
      size="xs"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span aria-current="page">{{ detail.breadcrumbLabel }} — {{ eventDate() }}</span>
  </nav>

  <header class="page-header flex justify-between align-start gap-20">
    <div class="page-header__heading">
      <div class="flex align-center gap-8">
        <h1 class="page-title fs-22 fw-700 m-0">{{ detail.title }}</h1>
        <span class="action-badge" [class]="actionBadgeClass()">{{ detail.actionBadge }}</span>
      </div>
      <p class="page-subtitle">{{ subline() }}</p>
    </div>

    <a class="secondary-button page-header__back fw-600" routerLink="/activity-logs">
      <app-icon
        name="chevron_left"
        size="sm"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Back to Audit Log</span>
    </a>
  </header>

  @if (detail.isFlagged && detail.severityNote) {
  <div class="flag-banner flex align-center gap-8" role="note">
    <app-icon
      class="flag-banner__icon"
      name="warning"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <span>{{ detail.severityNote }}</span>
  </div>
  }

  <section class="detail-card" aria-labelledby="event-details-title">
    <div class="detail-card__header">
      <h2 id="event-details-title" class="detail-card__title">Event Details</h2>
    </div>

    <div class="detail-card__body event-grid">
      <div class="field">
        <span class="field__label">Date &amp; Time</span>
        <span class="field__value">{{ eventDate() }}</span>
        <span class="field__secondary">{{ eventTime() }}</span>
      </div>

      @if (patient(); as patient) {
      <div class="field">
        <span class="field__label">Patient</span>
        <!-- Plain text until a patient screen exists to link `patientPortalUserId` to. -->
        <span class="field__value">{{ patient.displayName }}</span>
        @if (patient.medicalRecordNumber) {
        <span class="field__secondary">MRN: {{ patient.medicalRecordNumber }}</span>
        }
      </div>
      }

      <div class="field">
        <span class="field__label">Performed By</span>
        <!-- Plain text until an admin-user screen exists to link the acting admin id to. -->
        <span class="field__value">{{ detail.actorDisplayName }}</span>
        <span class="field__secondary">{{ detail.actorType }}</span>
      </div>

      @if (blockDetail(); as block) {
      <div class="field field--full">
        <span class="field__label">Block Reason</span>
        <span class="field__value">{{ block.reason ?? '—' }}</span>
      </div>

      @if (block.comment) {
      <div class="field field--full">
        <span class="field__label">Comment</span>
        <!-- Staff-authored free text: interpolated so it renders as text, never as HTML. -->
        <blockquote class="note-preview">{{ block.comment }}</blockquote>
      </div>
      } }
    </div>
  </section>

  @if (panelTitle(); as panelTitle) {
  <section class="detail-card" aria-labelledby="panel-title">
    <div class="detail-card__header">
      <h2 id="panel-title" class="detail-card__title">{{ panelTitle }}</h2>
    </div>

    <div class="detail-card__body panel-grid">
      @if (noteDetail(); as note) {
      <div class="field">
        <span class="field__label">Associated Request</span>
        <span class="field__value"
          >#{{ note.associatedRequestId }} — {{ note.associatedRequestType }}</span
        >
      </div>

      <div class="field">
        <span class="field__label">Note Added To</span>
        <span class="field__value">{{ note.noteAddedTo }}</span>
      </div>

      <div class="field">
        <span class="field__label">Note Length</span>
        <span class="field__value">{{ note.noteLength }} characters</span>
      </div>

      <div class="field">
        <span class="field__label">Visibility</span>
        <span class="field__value">{{ note.visibility }}</span>
      </div>

      @if (note.notePreview) {
      <div class="field field--full">
        <span class="field__label">Note Preview</span>
        <!-- Staff-authored free text: interpolated so it renders as text, never as HTML. -->
        <blockquote class="note-preview">{{ note.notePreview }}</blockquote>
        @if (note.isTruncated) {
        <p class="note-preview__truncation">
          <span>Preview only — this note is {{ note.noteLength }} characters.</span>
          <!--
            The anchor is withheld when the associated request has no detail screen we can resolve
            (a proxy request) — the notice still tells the user the preview is clipped.
          -->
          @if (fullNoteLink(); as fullNoteLink) {
          <a class="inline-link fw-600" [routerLink]="fullNoteLink"
            >View the full note on the request</a
          >
          }
        </p>
        }
      </div>
      } } @else if (decisionDetail(); as decision) {
      <div class="field">
        <span class="field__label">Request #</span>
        <span class="field__value">{{ decision.requestNumber }}</span>
      </div>

      <div class="field">
        <span class="field__label">Requester</span>
        <span class="field__value">{{ decision.requester }} ({{ decision.requesterType }})</span>
      </div>

      <div class="field">
        <span class="field__label">Decision</span>
        <span class="field__value" [class]="decisionClass()">{{ decision.decision }}</span>
      </div>

      <div class="field">
        <span class="field__label">Decision By</span>
        <span class="field__value">{{ decision.decisionBy }}</span>
      </div>

      @if (panel() === 'approval') { @if (decision.notes) {
      <div class="field field--full">
        <span class="field__label">Notes</span>
        <!-- Staff-authored free text: interpolated so it renders as text, never as HTML. -->
        <span class="field__value">{{ decision.notes }}</span>
      </div>
      } @if (decision.linkedTo) {
      <div class="field field--full">
        <span class="field__label">Linked To</span>
        <span class="field__value">{{ decision.linkedTo }}</span>
      </div>
      } } @else { @if (decision.denialReason) {
      <div class="field field--full">
        <span class="field__label">Reason</span>
        <span class="field__value">{{ decision.denialReason }}</span>
      </div>
      } @if (decision.followUp) {
      <div class="field field--full">
        <span class="field__label">Follow-up Required</span>
        <span class="field__value">{{ decision.followUp }}</span>
      </div>
      } } }
    </div>
  </section>
  } }
</section>
